import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Search, CheckCircle2, XCircle, Clock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_META = {
  valid: { label: "VALID", color: "text-[#4ADE80]", bg: "bg-[#4ADE80]/10 border-[#4ADE80]/40", Icon: CheckCircle2 },
  expired: { label: "EXPIRED", color: "text-[#B8A891]", bg: "bg-white/5 border-white/15", Icon: Clock },
  redeemed: { label: "REDEEMED", color: "text-[#D97645]", bg: "bg-[#D97645]/10 border-[#D97645]/40", Icon: CheckCircle2 },
};

const fmt = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return iso;
  }
};

export default function AdminDiscounts({ token }) {
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null); // {found, ...}
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const lookup = async (raw) => {
    const c = (raw ?? code).trim().toUpperCase();
    if (!c) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/discounts/lookup`, {
        ...auth,
        params: { code: c },
      });
      setResult(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Lookup failed");
      setResult(null);
    } finally {
      setLoading(false);
    }
  };

  const redeem = async () => {
    if (!result?.code) return;
    try {
      await axios.post(`${API}/admin/discounts/redeem`, { code: result.code }, auth);
      toast.success("Code redeemed");
      lookup(result.code);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Redeem failed");
    }
  };

  const meta = result?.status ? STATUS_META[result.status] : null;
  const StatusIcon = meta?.Icon;

  return (
    <div className="space-y-8" data-testid="admin-discounts">
      <div>
        <h3 className="font-display text-3xl md:text-4xl text-[#F2E7D0]">Discount Codes</h3>
        <p className="text-[#B8A891] text-sm mt-2 max-w-2xl">
          Paste a <span className="font-mono-label text-[#D97645]">TIKRA-XXXXXX</span> code that a
          returning guest is presenting at reception. Check who it belongs to, whether it is still
          valid, and mark it redeemed after honouring the 20% food discount.
        </p>
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          lookup();
        }}
        className="flex flex-col sm:flex-row gap-3 items-stretch max-w-2xl"
      >
        <div className="flex-1 relative">
          <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#B8A891]" />
          <Input
            data-testid="discount-code-input"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="TIKRA-XXXXXX"
            className="pl-10 h-12 bg-[#1C1913] border-white/15 text-[#F2E7D0] rounded-none font-mono-label tracking-[0.15em]"
            autoFocus
          />
        </div>
        <Button
          type="submit"
          data-testid="discount-lookup-btn"
          disabled={loading || !code.trim()}
          className="h-12 rounded-none bg-[#D97645] hover:bg-[#C0602F] text-[#F2E7D0] font-mono-label px-8"
        >
          {loading ? "Looking up…" : "Look up →"}
        </Button>
      </form>

      {/* Empty state */}
      {!result && !loading && (
        <div className="border border-dashed border-white/10 p-10 text-center text-[#B8A891]/70 text-sm">
          Enter a code above to see the guest, the original booking, and expiry.
        </div>
      )}

      {/* Not found */}
      {result && !result.found && (
        <div
          data-testid="discount-not-found"
          className="border border-white/15 bg-white/5 p-6 flex items-start gap-4"
        >
          <XCircle size={20} className="text-[#B8A891] mt-0.5" />
          <div>
            <div className="font-mono-label text-[#F2E7D0] mb-1">No booking found for this code</div>
            <div className="text-[#B8A891] text-sm">
              We do not have <span className="font-mono-label text-[#D97645]">{result.code}</span>{" "}
              in our records. Double-check the spelling — codes look like TIKRA-A1B2C3.
            </div>
          </div>
        </div>
      )}

      {/* Result */}
      {result && result.found && meta && (
        <div
          data-testid="discount-result"
          data-status={result.status}
          className={`border ${meta.bg} p-6 md:p-8 space-y-6`}
        >
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="font-mono-label text-[10px] tracking-[0.3em] text-[#B8A891] mb-2">
                CODE
              </div>
              <div className="font-mono-label text-2xl md:text-3xl text-[#F2E7D0] tracking-[0.25em]">
                {result.code}
              </div>
              <div className="font-mono-label text-[10px] tracking-[0.28em] text-[#B8A891] mt-3">
                {result.discount_percent}% OFF · {String(result.discount_scope || "").toUpperCase()}
              </div>
            </div>
            <div className={`flex items-center gap-2 ${meta.color} font-mono-label text-sm`}>
              <StatusIcon size={16} />
              <span>{meta.label}</span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-4 border-t border-white/10 pt-6">
            <Row label="Guest" value={result.guest?.name || "—"} />
            <Row label="Email" value={result.guest?.email || "—"} />
            <Row label="Phone" value={result.guest?.phone || "—"} />
            <Row label="Original booking" value={result.issued_for_booking?.id || "—"} mono />
            <Row label="Original stay" value={
              result.issued_for_booking?.check_in
                ? `${result.issued_for_booking.check_in} → ${result.issued_for_booking.check_out}`
                : "—"
            } />
            <Row label="Issued on" value={fmt(result.issued_at)} />
            <Row label="Expires on" value={fmt(result.expires_at)} />
            {result.redeemed_at && (
              <Row label="Redeemed on" value={fmt(result.redeemed_at)} />
            )}
          </div>

          {result.status === "valid" && (
            <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-white/10">
              <Button
                onClick={redeem}
                data-testid="discount-redeem-btn"
                className="rounded-none bg-[#D97645] hover:bg-[#C0602F] text-[#F2E7D0] font-mono-label h-12 px-8"
              >
                Mark as redeemed →
              </Button>
              <Button
                onClick={() => { setResult(null); setCode(""); }}
                variant="ghost"
                className="rounded-none border border-white/15 text-[#F2E7D0] font-mono-label h-12 px-8 hover:bg-white/5"
              >
                Look up another
              </Button>
            </div>
          )}
          {result.status !== "valid" && (
            <div className="pt-4 border-t border-white/10">
              <Button
                onClick={() => { setResult(null); setCode(""); }}
                variant="ghost"
                className="rounded-none border border-white/15 text-[#F2E7D0] font-mono-label h-12 px-8 hover:bg-white/5"
              >
                Look up another
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Row({ label, value, mono }) {
  return (
    <div>
      <div className="font-mono-label text-[10px] tracking-[0.28em] text-[#B8A891] mb-1">
        {label.toUpperCase()}
      </div>
      <div className={`text-[#F2E7D0] ${mono ? "font-mono-label text-sm" : ""}`}>{value}</div>
    </div>
  );
}
